/**
 * Constantes de juice (E§1).
 *
 * Même statut que `balance.ts` : **toute durée et toute amplitude d'animation vit ici**,
 * jamais en dur dans la logique. Le ressenti se règle sans rouvrir un fichier de moteur.
 *
 * Les durées de transition d'interface, elles, restent dans `ui/tokens.ts` : ce sont des
 * jetons de design partagés avec la maquette. Ici on décrit le mouvement du JEU.
 */

export const JUICE = {
  /** §1.1 — pose d'un bâtiment. */
  placement: {
    /** La tuile cible s'enfonce et s'éclaircit avant l'apparition. */
    anticipationMs: 80,
    anticipationDepthPx: 2,
    /** Hauteur de chute du bâtiment, en pixels monde. */
    dropHeightPx: 40,
    dropMs: 220,
    /** Écrasement/étirement à l'impact. */
    squashScaleX: 1.15,
    squashScaleY: 0.85,
    reboundMs: 320,
    /** Anneau de poussière. */
    shockwaveMs: 400,
    /** Sursaut des bâtiments adjacents, et son décalage. */
    neighbourJumpPx: 3,
    neighbourDelayMs: 60,
    /**
     * B.2 — les voisins S'ÉCARTENT du fantôme pendant le survol : un retour tactile
     * sans un mot de texte (LIVRAISON 0.2, dernier point). L'amplitude reste petite —
     * la ville fait de la place, elle ne fuit pas.
     */
    leanPx: 7,
    leanMs: 150,
    /** Rayon de l'effet autour de l'emprise du fantôme, en tuiles. */
    leanRadiusTiles: 1.3,
  },

  /** §1.2 — gains de pièces. */
  coins: {
    flightMs: 620,
    /** Hauteur de l'arc de Bézier, en fraction de la distance parcourue. */
    arcHeightRatio: 0.28,
    /** Décalage entre deux pièces d'une même volée. */
    staggerMs: 45,
    /** Pulsation du compteur à l'arrivée. */
    counterPulseScale: 1.12,
    /**
     * Au-delà de ce nombre de pièces par seconde, on cesse d'en faire voler une par
     * gain : une pièce « grosse » agrégée les remplace. Le juice ne doit jamais coûter
     * des fps (E§1.2).
     */
    maxFlightsPerSecond: 12,
    /** Plafond dur de pièces simultanément en vol, quelle que soit la production. */
    maxConcurrentFlights: 40,
    /**
     * Cadence MINIMALE entre deux sons de pièce, et volume relatif.
     *
     * Le son de pièce n'est pas l'événement « une pièce est arrivée » mais l'événement
     * « on encaisse » : une ville qui produit dix fois par seconde produirait sinon un
     * crépitement continu. Le limiteur général (4 sons / 200 ms) ne suffit pas — il
     * empêche la saturation, pas la répétition. Une seconde entre deux tintements, à
     * volume réduit, fait de la pièce un accent et non un fond sonore.
     */
    soundIntervalMs: 1000,
    soundGain: 0.3,
  },

  /**
   * B.1 — le nombre qui gicle du bâtiment à chaque encaissement visible.
   *
   * C'est LE signal des idle games : la production doit se VOIR sur la ville, pas se
   * lire dans un compteur. Le nombre accompagne la pièce — même événement, même
   * plafond de débit — mais monte au lieu de partir vers le HUD : l'œil peut suivre
   * l'un ou l'autre sans que les deux se disputent le même trajet.
   */
  gainLabel: {
    /** Montée puis évanouissement, en une seule durée. */
    floatMs: 900,
    /** Hauteur de montée, en pixels écran. */
    risePx: 46,
    /** Dérive horizontale maximale (tirée par nombre), pour casser l'alignement. */
    driftPx: 14,
    /**
     * Taille = base + croissance × log10(montant). Un gain de 10 pièces et un gain
     * d'un milliard ne racontent pas la même histoire : le logarithme est la seule
     * échelle qui reste lisible sur les trente ordres de grandeur d'un idle.
     */
    baseFontPx: 13,
    growthFontPx: 2.6,
    maxFontPx: 34,
  },

  /** §1.3 — achat impossible. */
  refusal: {
    shakeMs: 220,
    shakeOscillations: 3,
    shakeAmplitudePx: 7,
  },

  /** §1.4 — jalon atteint. */
  milestone: {
    /** Léger ralenti du monde, pas un gel : le jeu continue de respirer. */
    freezeMs: 120,
    freezeTimeScale: 0.15,
    burstParticles: 26,
    burstMs: 700,
    /** Confettis plein écran : une seule fois par session, la rareté fait l'effet. */
    confettiParticles: 90,
    confettiMs: 2200,
  },

  /** §1.5 — extension de terrain. */
  landExpansion: {
    /** Recadrage doux de la caméra, jamais de téléportation. */
    cameraMs: 700,
    /** Décalage de dépliage entre deux tuiles voisines. */
    tileStaggerMs: 25,
    tileDropMs: 260,
  },

  /** §1.6 — prestige. */
  prestige: {
    desaturateMs: 800,
    flashMs: 120,
    shakeMs: 300,
    shakeAmplitudePx: 9,
    rebirthMs: 900,
    /** Le multiplicateur reste ancré dans le HUD, pulsant, après la séquence. */
    multiplierHoldMs: 10_000,
    /**
     * B.3 — l'envol de la ville avant le reset. Durée TOTALE, quel que soit le
     * nombre de bâtiments : les départs se répartissent dans la fenêtre. Un adieu,
     * pas une pénitence.
     */
    farewellMs: 1100,
    farewellRisePx: 240,
  },

  /** §1.7 — boost de visite reçu. */
  boost: {
    bannerMs: 400,
    /**
     * Saturation ajoutée à la ville pendant toute la durée du boost. Appliquée par
     * `tint` de sprite et non par filtre plein écran : un filtre maintenu deux heures
     * sur mobile mange la batterie pour le même résultat.
     */
    saturationBoost: 0.1,
    goldenParticlesPerSecond: 3,
    vehicleSpeedMultiplier: 1.4,
  },

  /** §1.8 — vie ambiante permanente. */
  ambient: {
    /** Respiration des bâtiments : amplitude et période, déphasées par bâtiment. */
    breathAmplitudePx: 1,
    breathPeriodMs: 4200,
    /**
     * La respiration est recalculée à cette fréquence, pas à chaque frame : 200 sprites
     * transformés 60 fois par seconde interdisent tout regroupement de rendu.
     */
    breathUpdateHz: 20,
    birdIntervalMinMs: 30_000,
    birdIntervalMaxMs: 60_000,
  },

  /**
   * B.7 — la vitrine de la boutique : la carte sélectionnée tourne lentement sur ses
   * quatre orientations. Un tour complet en ~3,6 s — on présente, on n'agite pas.
   */
  showcase: {
    rotateMs: 900,
  },

  /**
   * B.6 — la pluie de pièces du retour.
   *
   * L'écran « Content de te revoir » fait pleuvoir des pièces proportionnellement au
   * gain — sur une échelle LOG, comme le nombre qui gicle — qui s'aspirent vers le
   * compteur. Trois secondes, une seule fois, à la fermeture de l'écran : pendant
   * qu'il est ouvert, le joueur lit.
   */
  returnRain: {
    /** Plancher et plafond de pièces, l'échelle log fait le reste. */
    minCoins: 6,
    maxCoins: 22,
    coinsPerMagnitude: 2,
    /** Espacement des départs — sous le plafond de débit du vol de pièces. */
    staggerMs: 140,
  },

  /**
   * B.5 — secousse d'écran DOSÉE.
   *
   * Réservée aux trois événements rares — prestige, premier jalon de la session,
   * extension de terrain — et jamais à la production courante : la secousse
   * permanente est ce qui rend les faux jeux épuisants. Petite et brève par
   * conception ; le prestige garde sa propre amplitude (`prestige.shake*`).
   */
  shake: {
    amplitudePx: 3,
    durationMs: 140,
    oscillations: 3,
  },

  /** Politique de mouvement réduit (E§1, règle commune). */
  reducedMotion: {
    /**
     * En mouvement réduit, une animation s'applique instantanément — mais elle
     * s'applique. Jamais d'absence de retour : le joueur doit toujours savoir que
     * son geste a été pris en compte.
     */
    instant: true,
  },
} as const;
